import { useCallback, useEffect, useRef, useState } from 'react';
import { useOptionalActiveSession } from '../api/active/sessionProvider';
import type { ActiveTransport } from '../api/active/transport';
import { useCatalogSessionScope } from '../quote-integration/useCatalogSessionScope';
import { canReadFieldMetadata } from './fieldMetadataAccess';

export type FieldDisplayName = {
  id: string;
  resourceType: 'quote' | 'quote_item' | 'customer';
  groupKey: string;
  fieldKey: string;
  labelZh: string;
  defaultLabelZh: string;
  rowVersion: string;
};

export function fieldDisplayNameKey(
  resourceType: FieldDisplayName['resourceType'],
  groupKey: string,
  fieldKey: string,
) {
  return `${resourceType}\u0000${groupKey}\u0000${fieldKey}`;
}

let cache = new WeakMap<
  ActiveTransport,
  Map<
    string,
    {
      until: number;
      request: Promise<FieldDisplayName[]>;
    }
  >
>();
const listeners = new Set<() => void>();
let focusListenerAttached = false;

async function readFieldDisplayNames(
  transport: ActiveTransport,
  scope: string,
): Promise<FieldDisplayName[]> {
  let scoped = cache.get(transport);
  if (!scoped) {
    scoped = new Map();
    cache.set(transport, scoped);
  }
  const existing = scoped.get(scope);
  if (existing && existing.until > Date.now()) return existing.request;
  const request = transport
    .requestResponse('/field-display-names', { cache: 'no-store' })
    .then(async (response) => {
      if (!response.ok)
        throw new Error(`字段名称读取失败（HTTP ${response.status}）`);
      return ((await response.json()) as { items: FieldDisplayName[] }).items;
    });
  scoped.set(scope, { until: Date.now() + 30_000, request });
  request.catch(() => {
    if (scoped.get(scope)?.request === request) scoped.delete(scope);
  });
  return request;
}

export function invalidateFieldDisplayNames() {
  cache = new WeakMap();
  for (const listener of listeners) listener();
}

export function subscribeFieldDisplayNamesInvalidation(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useFieldDisplayNames(enabled = true) {
  const session = useOptionalActiveSession();
  const transport = session?.transport;
  const permitted =
    session?.state.status === 'ready' &&
    canReadFieldMetadata(session.state.user, undefined, 'display-names');
  const ready = enabled && permitted;
  const scope = useCatalogSessionScope() ?? '';
  const [items, setItems] = useState<FieldDisplayName[]>([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    if (!transport || !ready) return;
    setStatus('loading');
    try {
      const items = await readFieldDisplayNames(transport, scope);
      if (current !== generation.current) return;
      setItems(items);
      setError('');
      setStatus('ready');
    } catch (cause) {
      if (current !== generation.current) return;
      setError(cause instanceof Error ? cause.message : '字段名称读取失败');
      setStatus('error');
    }
  }, [ready, scope, transport]);
  useEffect(() => {
    if (!enabled || !permitted) {
      generation.current++;
      setItems([]);
      setError('');
      setStatus('idle');
      return;
    }
    if (!focusListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('focus', invalidateFieldDisplayNames);
      focusListenerAttached = true;
    }
    void reload();
    const invalidate = () => {
      void reload();
    };
    const unsubscribe = subscribeFieldDisplayNamesInvalidation(invalidate);
    return () => {
      generation.current++;
      unsubscribe();
    };
  }, [enabled, permitted, reload]);
  const overrides = new Map(
    items
      .filter((item) => item.rowVersion !== '0')
      .map((item) => [
        fieldDisplayNameKey(item.resourceType, item.groupKey, item.fieldKey),
        item.labelZh,
      ]),
  );
  return {
    items,
    error,
    loading: ready && (status === 'idle' || status === 'loading'),
    ready: ready && status === 'ready',
    reload,
    overriddenLabel(
      resourceType: FieldDisplayName['resourceType'],
      groupKey: string,
      fieldKey: string,
    ) {
      return overrides.get(
        fieldDisplayNameKey(resourceType, groupKey, fieldKey),
      );
    },
    label(
      resourceType: FieldDisplayName['resourceType'],
      groupKey: string,
      fieldKey: string,
      fallback: string,
    ) {
      return (
        overrides.get(fieldDisplayNameKey(resourceType, groupKey, fieldKey)) ??
        fallback
      );
    },
  };
}
